import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LineMask } from "./Reveal";
import { HERO } from "@/constants/testIds";
import { SITE } from "@/data/siteData";
import { ArrowRight, Phone } from "lucide-react";

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });

    const pizzaY = useTransform(scrollYProgress, [0, 1], [0, -160]);
    const pizzaR = useTransform(scrollYProgress, [0, 1], [-8, 40]);
    const signY = useTransform(scrollYProgress, [0, 1], [0, -260]);
    const signR = useTransform(scrollYProgress, [0, 1], [-15, -30]);
    const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid={HERO.section}
            className="relative min-h-[100svh] pt-28 md:pt-36 pb-16 overflow-hidden"
        >
            {/* Floating One Way sign */}
            <motion.img
                data-testid={HERO.floatingSign}
                src="https://images.unsplash.com/photo-1483213097419-365e22f0f258?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxvbmUlMjB3YXklMjBzdHJlZXQlMjBzaWdufGVufDB8fHx8MTc4NDUzMTkxMXww&ixlib=rb-4.1.0&q=85"
                alt="One Way"
                style={{ y: signY, rotate: signR }}
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.6, duration: 1, ease: [0.2, 0.9, 0.2, 1] }}
                className="hidden md:block absolute top-[18%] right-[6%] w-[220px] h-[220px] object-cover rounded-2xl border-2 border-[#0a0a0a] shadow-[10px_10px_0_0_#0a0a0a] z-10"
            />

            {/* Floating pizza */}
            <motion.div
                data-testid={HERO.floatingPizza}
                style={{ y: pizzaY, rotate: pizzaR }}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.9, duration: 1.1, ease: [0.2, 0.9, 0.2, 1] }}
                className="absolute -bottom-16 md:bottom-[6%] -left-8 md:left-[8%] w-[240px] md:w-[380px] h-[240px] md:h-[380px] rounded-full overflow-hidden border-2 border-[#0a0a0a] shadow-[12px_12px_0_0_#0a0a0a] z-10"
            >
                <img
                    src="https://images.pexels.com/photos/29121112/pexels-photo-29121112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Pizza"
                    className="w-full h-full object-cover"
                />
            </motion.div>

            {/* Big yellow + green accents */}
            <div className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#FFD600] opacity-90 z-0" />
            <div className="pointer-events-none absolute top-[65%] right-[18%] w-[180px] h-[180px] rounded-full bg-[#7BB53E] opacity-90 z-0" />
            <div className="pointer-events-none absolute top-[60%] left-[35%] w-[220px] h-[220px] rounded-full bg-[#1E40FF] opacity-15 blur-3xl z-0" />

            <motion.div style={{ y: contentY }} className="relative z-20 px-5 md:px-10 lg:px-14 max-w-[1600px] mx-auto">
                <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                        className="inline-block w-2.5 h-2.5 rounded-full bg-[#7BB53E]"
                    />
                    <LineMask delay={0.35} duration={0.7}>
                        <span className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold">
                            Pizza al Taglio & Rotonda · Castel d&apos;Ario · Via XX Settembre 26
                        </span>
                    </LineMask>
                </div>

                <h1
                    data-testid={HERO.titleLine1}
                    className="font-display uppercase leading-[0.82] tracking-tighter text-[22vw] md:text-[16vw] lg:text-[14vw] text-[#0a0a0a] select-none"
                >
                    <div className="line-mask block">
                        <motion.span
                            className="block"
                            initial={{ y: "110%" }}
                            animate={{ y: "0%" }}
                            transition={{ delay: 0.6, duration: 1, ease: [0.2, 0.9, 0.2, 1] }}
                        >
                            SENSO
                        </motion.span>
                    </div>
                    <div data-testid={HERO.titleLine2} className="line-mask block">
                        <motion.span
                            className="block text-[#E63946] italic font-serif-editorial normal-case tracking-tight"
                            initial={{ y: "110%" }}
                            animate={{ y: "0%" }}
                            transition={{ delay: 0.85, duration: 1, ease: [0.2, 0.9, 0.2, 1] }}
                            style={{ letterSpacing: "-0.03em" }}
                        >
                            unico<span className="text-[#0a0a0a] font-display not-italic">.</span>
                        </motion.span>
                    </div>
                </h1>

                <div className="mt-10 md:mt-16 grid md:grid-cols-12 gap-6 items-end">
                    <div className="md:col-span-6 lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.1, duration: 0.8 }}
                            className="flex flex-wrap items-center gap-4"
                        >
                            <a
                                href={`tel:${SITE.phoneRaw}`}
                                data-testid={HERO.ctaCall}
                                className="hard-shadow inline-flex items-center gap-3 px-6 py-4 rounded-full bg-[#E63946] text-white border-2 border-[#0a0a0a] font-bold uppercase tracking-widest text-sm"
                            >
                                <Phone className="w-4 h-4" strokeWidth={2.5} /> Chiama Ora
                            </a>
                            <button
                                data-testid={HERO.ctaMenu}
                                onClick={() =>
                                    document.getElementById("menu")?.scrollIntoView({
                                        behavior: "smooth",
                                    })
                                }
                                className="hard-shadow-sm inline-flex items-center gap-3 px-6 py-4 rounded-full bg-white text-[#0a0a0a] border-2 border-[#0a0a0a] font-bold uppercase tracking-widest text-sm"
                            >
                                Vedi il Menu <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                            </button>
                        </motion.div>
                    </div>

                    <div className="hidden md:block md:col-span-5 md:col-start-8 lg:col-span-4 lg:col-start-9">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.6, duration: 0.8 }}
                            className="border-t-2 border-[#0a0a0a] pt-4"
                        >
                            <div className="flex items-baseline justify-between">
                                <span className="text-xs uppercase tracking-[0.25em] font-bold">
                                    Al Taglio
                                </span>
                                <span className="font-display text-4xl text-[#5A8A2B]">
                                    € {SITE.tagliePrice}
                                </span>
                            </div>
                            <div className="flex items-baseline justify-between mt-2 border-t border-[#0a0a0a]/30 pt-2">
                                <span className="text-xs uppercase tracking-[0.25em] font-bold">
                                    Rotonda
                                </span>
                                <span className="font-display text-2xl">
                                    da € 3,00
                                </span>
                            </div>
                            <div className="flex items-baseline justify-between mt-2 border-t border-[#0a0a0a]/30 pt-2">
                                <span className="text-xs uppercase tracking-[0.25em] font-bold">
                                    Gusti
                                </span>
                                <span className="font-display text-2xl">70+</span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.div>

            {/* Bottom scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2, duration: 1 }}
                className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-20 items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold"
            >
                <span className="inline-block w-8 h-[2px] bg-[#0a0a0a]" />
                Scorri per assaggiare
                <span className="inline-block w-8 h-[2px] bg-[#0a0a0a]" />
            </motion.div>
        </section>
    );
};

export default Hero;
